import React, { useState } from 'react';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Badge } from '../components/badge';

const tagColors = {
  bug: 'red',
  conocido: 'orange',
  mejora: 'green',
  comisiones: 'blue',
  configuración: 'sky',
  ux: 'pink',
  desarrollo: 'amber',
  alta: 'purple',
  media: 'yellow',
  baja: 'zinc',
};

const enDesarrollo = [
  {
    title: 'Exportación de reportes en CSV y PDF con el detalle completo',
    tags: ['desarrollo'],
    priority: 'alta',
    description: 'Exportación de todos los reportes, no solo de algunos: en CSV con el detalle movimiento por movimiento —afianzadora, cliente, convenio, contrato, fianza, prima, comisión y estado de cobro— para trabajarlo en Excel, y en PDF con tablas reales en lugar de capturas de pantalla, sin cortes ni límite de filas. Los archivos indican qué filtros se aplicaron, qué comisiones están por confirmar y que los montos son sin IVA.',
  },
  {
    title: 'Lectura automática de los documentos del contrato',
    tags: ['desarrollo'],
    priority: 'alta',
    description: 'Extracción automática de los datos de los documentos que se adjuntan al contrato —convenios modificatorios, actas de entrega y documentos de anulación— con prellenado del endoso o del evento que corresponda.',
  },
  {
    title: 'Sistema de notificaciones',
    tags: ['desarrollo'],
    priority: 'alta',
    description: 'Lo que falta de los avisos: notificaciones dentro de la plataforma, además del correo, y las alertas de fianzas diferidas y después del término.',
  },
  {
    title: 'Bonos de comisión de las afianzadoras',
    tags: ['desarrollo'],
    priority: 'alta',
    description: 'Registro de los bonos que varias afianzadoras otorgan además de la comisión —de maquila, de producción y de cancelación— y su reflejo en los reportes de comisiones.',
  },
  {
    title: 'Eliminación de clientes',
    tags: ['desarrollo'],
    priority: 'baja',
    description: 'Funcionalidad para eliminar clientes del sistema.',
  },
  {
    title: 'Eliminación de links',
    tags: ['desarrollo'],
    priority: 'baja',
    description: 'Funcionalidad para eliminar links de expediente.',
  },
];

const problemasConocidos = [
  {
    title: 'Lectura de XML de afianzadoras no verificadas',
    date: '3 Feb 2026',
    tags: ['conocido'],
    summary:
      'No podemos asegurar lectura 100% correcta de XMLs de fianzas que no sean de Berkley, Tokio Marine, Aserta o Asecam.',
    bullets: [
      'Los XMLs de estas cuatro afianzadoras han sido verificados y se leen correctamente.',
      'XMLs de otras afianzadoras podrían tener estructuras diferentes que causen lecturas parciales o incorrectas.',
      'Si detectas datos faltantes o incorrectos en una fianza de otra afianzadora, por favor repórtalo.',
    ],
  },
  {
    title: 'Cálculo de comisión en tarifas fuera de rango',
    date: '23 Dic 2025',
    tags: ['bug', 'conocido'],
    summary:
      'IMPORTANTE: Existe un problema donde si la tarifa de una fianza está por DEBAJO de todos los rangos configurados en la tabla, el sistema asigna incorrectamente el tramo más bajo en lugar de no asignar comisión.',
    bullets: [
      'Si tarifa es menor al límite inferior del tramo más bajo, debería devolver sin comisión (N/A).',
      'Actualmente aplica el tramo más bajo aunque la tarifa no caiga en ese rango.',
      'Ejemplo: Tarifa 0.25% con tabla que empieza en 0.5% asigna comisión cuando no debería.',
      'Se está trabajando en la corrección para respetar los rangos configurados estrictamente.',
    ],
  },
];

const actualizaciones = [
  {
    title: 'Órdenes de trabajo',
    date: '19 Oct 2026',
    tags: ['mejora', 'configuración', 'ux'],
    summary:
      'Una superficie de trabajo para el equipo que opera el trámite: cada fianza, endoso o cualquier otro tipo de trámite que la organización defina lleva una orden con folio, fases, equipos, responsables, pendientes e hilo de conversación. Se activa por organización; si no ves Órdenes de trabajo en el menú, pídelo a Surelia.',
    bullets: [
      'Las órdenes se crean a mano, con tipo, fiado, contrato y fianza; el contrato se busca por número y muestra cuándo se subió. Pueden nacer sin contrato y recibirlo después. No llevan nombre propio: el folio, el tipo y el fiado las identifican.',
      'Configuración, pestaña Órdenes de trabajo: equipos con sus miembros, tipos de orden con sus fases, el equipo responsable y los días objetivo de cada fase, coordinadores y aprobadores. Todo se guarda solo. Fianza y Endoso vienen de base; los demás tipos los define la organización.',
      'Quien trabaja una fase la marca terminada; el coordinador la avanza a la siguiente eligiendo quién la trabaja, con la carga actual de cada persona y la etiqueta de ejecutivo del fiado a la vista. Los responsables son por fase y el historial guarda quién trabajó cada una.',
      'Fases de aprobación: nadie las termina, un aprobador aprueba o rechaza con motivo. Cada decisión queda registrada en la orden, en el hilo y en la pestaña Aprobaciones. Solo los aprobadores pueden escribir esa decisión.',
      'Tablero por tipo de orden, lista con fechas de inicio y de fase, bandeja del coordinador con lo que terminó su fase y lo que no tiene responsable, carga por persona y por equipo, y estadísticas de tiempos reales contra objetivo. Carga, estadísticas y bandeja son del coordinador.',
      'Hilo por orden con los mensajes del equipo y los eventos del sistema; @"Nombre" etiqueta a cualquiera de la organización y le llega a la campana. La campana de notificaciones avisa de asignaciones, cambios de fase, aprobaciones y menciones, y abre la orden.',
      'Pendientes dentro de la orden, con equipo destinatario opcional. En el fiado, el contrato y la fianza se ven sus órdenes activas. El cierre es a mano, documentado, y nada se borra; una orden cerrada se puede reabrir con motivo.',
    ],
  },
  {
    title: 'Reportes: aviso cuando no cabe',
    date: '19 Oct 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Un reporte más ancho que la ventana ya no se encima al menú. Aparece un aviso para colapsar la barra lateral, quitar el zoom o agrandar la ventana, y los filtros de Cobranza de primas tienen su propia fila.',
    bullets: [],
  },
  {
    title: 'Configuración y detalle de cliente sin barra de scroll en las pestañas',
    date: '19 Oct 2026',
    tags: ['ux', 'configuración'],
    summary:
      'Las secciones de Configuración y del cliente caben completas en pantalla, sin desplazarse de lado para encontrar una pestaña.',
    bullets: [
      'Configuración se navega desde un menú a la izquierda agrupado en Personal, Organización y Cuenta. Cada grupo muestra solo las secciones que tu organización tiene activas. En celular, las secciones se eligen desde un selector arriba de la página.',
      'En el cliente las pestañas llevan nombres cortos: General, Expediente, Contratos, Estadísticas, Afianzadoras, Asociados y Bitácora.',
      'Si la pantalla es angosta, Estadísticas, Afianzadoras y Asociados pasan a un menú "Más" al final de la barra. La pestaña que estás viendo siempre queda visible.',
    ],
  },
  {
    title: 'Pendientes: recordar al responsable',
    date: '19 Oct 2026',
    tags: ['mejora', 'ux'],
    summary:
      'El correo de cada pendiente va al equipo: un recordatorio a quien lo tiene asignado. Pedirle algo al fiado por correo queda solo donde estás viendo al fiado.',
    bullets: [
      'Recordar al responsable manda el pendiente por correo a sus responsables, con un mensaje opcional. Si no tiene a nadie asignado, eliges a quién de tu organización; solo se puede mandar a miembros del equipo.',
      'Las respuestas al recordatorio te llegan a ti. Un recordatorio por pendiente cada hora; quien se dio de baja de los correos de pendientes no lo recibe.',
      'En Órdenes de Trabajo y en la página de Pendientes el sobre solo recuerda al equipo; ahí ya no hay correo al fiado.',
      'En el fiado, el contrato y la fianza el sobre ofrece las dos opciones: recordar al responsable o solicitar al fiado.',
    ],
  },
  {
    title: 'Asociados: reparto de la comisión entre socios y referidores',
    date: '19 Oct 2026',
    tags: ['mejora', 'comisiones', 'configuración'],
    summary:
      'Repartir la comisión sobre prima neta de cada cliente entre los socios del despacho y los asociados que traen el negocio, con lo que le toca a cada quien mes a mes. Se activa por organización; si no ves la pestaña Asociados en Configuración, pídelo a Surelia.',
    bullets: [
      'Directorio en Configuración, pestaña Asociados: nombre, correo y tipo. Un socio es dueño del despacho y su parte se reporta; un asociado es un tercero que refiere negocio y su parte se le paga. Las bajas conservan el historial.',
      'En cada cliente, pestaña Asociados: el reparto sobre prima neta en una tabla con todos los asociados prefilados, que debe sumar 100%. Cada fianza puede llevar su propio reparto desde el editor de comisión, sin tocar el del cliente.',
      'Cada movimiento del libro de comisiones guarda el reparto con el que se registró. El reparto corta la comisión del despacho en partes; no cambia el monto ni el estado de cobro.',
      'Al cambiar un reparto, un diálogo lista las fianzas ya registradas con su fecha de emisión, fiado y afianzadora, y lo que cambiaría para cada asociado. Se eligen una por una, todas o ninguna, o a partir de una fecha. Lo que no se ajuste queda señalado en el cliente como "fianzas con reparto anterior" hasta que se revise.',
      'Reporte Comisión por asociado, en Reportes dentro de Comisiones: por mes, cuánto produjo cada socio y asociado y qué le toca, con el detalle por fianza; por año, la matriz de doce meses con totales. A los asociados se les marca pagado por mes; si el monto cambia después, el reporte muestra el ajuste.',
      'Aprobación por correo, opcional por organización: un cambio de reparto queda como propuesta y cada socio elegido recibe un correo con el antes y el después y un botón para aprobar o rechazar, sin iniciar sesión. Basta uno o se requieren todos, según se configure. El listado de aprobaciones en Configuración muestra cada propuesta con fecha, cliente, quién debía aprobar y qué respondió.',
      'Todo cambio de reparto, alta, baja y aprobación queda en la bitácora del cliente con los porcentajes anteriores y nuevos.',
    ],
  },
  {
    title: 'Resúmenes por correo: lo que te falta y lo que pasó',
    date: '28 Sep 2026',
    tags: ['mejora', 'configuración'],
    summary:
      'Tres correos configurables por miembro desde Configuración, pestaña Notificaciones: "Lo que te falta", "Lo que pasó" y "Te asignaron". Se activa por organización; si no ves la pestaña, pídelo a Surelia.',
    bullets: [
      '"Lo que te falta", semanal o diario: pendientes abiertos, fianzas vencidas y que vencen en 30 días, facturas sin pagar y fianzas requeridas sin iniciar. El owner ve toda la cartera; el operador, lo suyo. Separa lo nuevo desde el último resumen de lo que sigue igual, y el diario no se manda si no hay nada.',
      '"Lo que pasó", semanal para owners el día que fije la organización (lunes por default): emisión, prima, monto afianzado, cobranza, comisiones cobradas, cartera y avance de metas de la semana. Un interruptor por organización lo manda sin montos, para bandejas que se reenvían.',
      '"Te asignaron", al momento en que un compañero te asigna un pendiente.',
      'La organización fija la hora y la zona horaria; cada miembro elige frecuencia y qué recibe. El owner asigna los correos desde la rejilla, con el correo de cada miembro a la vista y un botón de prueba por persona.',
      'La baja personal manda: quien se dio de baja de un tipo de correo no lo recibe aunque la organización se lo asigne.',
    ],
  },
  {
    title: 'Pendientes',
    date: '28 Sep 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Una sección para lo que falta y no es un documento del expediente: actas de entrega, convenios modificatorios, compromisos con la afianzadora para poder emitir, y lo que el despacho trae entre manos sin relación con un fiado. Está en el menú junto a Pulso y como burbuja abajo a la derecha en toda la plataforma.',
    bullets: [
      'Cada pendiente tiene tipo, título, fecha límite, responsables y notas internas del equipo. El tipo decide a qué se liga: compromisos y documentos al fiado, actas al contrato o al fiado, convenios modificatorios al contrato, y "otro" a lo que sea o a nada.',
      'La burbuja muestra los pendientes del fiado que tienes abierto, los tuyos, los del equipo y los compromisos con afianzadoras, y da de alta uno nuevo con Enter sin salir de donde estás.',
      'El detalle del contrato tiene su panel de actas y convenios modificatorios; el de la fianza, sus compromisos con la afianzadora.',
      'La página agrupa por vencidos, esta semana, próximos 30 días, sin fecha y compromisos, y filtra por fiado, tipo, estatus y responsable. El historial conserva los resueltos y cancelados.',
      'Botón para pedir al fiado por correo lo que le toca entregar: actas, convenios modificatorios, documentos y otros. Los compromisos con la afianzadora no se mandan; se resuelven con ella. Un correo por pendiente cada 4 horas.',
      'Las solicitudes de endoso siguen en la fianza y aparecen en la misma lista; ahora se pueden cancelar sin emitir el endoso, con motivo, y asignar a un compañero.',
      'La pestaña "Endosos Pendientes" de Pulso ahora es "Pendientes" y muestra todo junto; la columna "Pendientes" del tablero se llama "Sin iniciar".',
      'Reportes tiene un reporte de pendientes por fiado, responsable, tipo o afianzadora, con abiertos, vencidos y el más antiguo. En Inicio, "Requiere tu atención" incluye los pendientes abiertos y los compromisos.',
      'Los encabezados de todas las tablas de reportes, Pulso y Pendientes van en azul Surelia.',
    ],
  },
  {
    title: 'Comparativo anual',
    date: '15 Sep 2026',
    tags: ['mejora', 'comisiones'],
    summary:
      'Un reporte que pone el ejercicio contra el anterior al mismo día de corte: si vas arriba o abajo, en qué mes se abrió la brecha y quién la explica. Está en Reportes, dentro de Comisiones.',
    bullets: [
      'El corte comparable es lo que hace que la cifra signifique algo. Con "A la fecha" los dos años se truncan al mismo mes y día, así que en septiembre compara enero al 15 de septiembre contra ese mismo tramo del año pasado; con "Año completo" compara los doce meses.',
      'Resumen: comisión, prima, monto afianzado, fianzas, clientes, comisión promedio por fianza y prima promedio por fianza, cada una con la cifra del año anterior y su variación, más la curva acumulada de los dos años.',
      'Variación: de dónde viene la diferencia, por afianzadora, cliente, responsable o tipo de fianza, ordenado por tamaño del movimiento y con la parte del total que aporta cada renglón.',
      'Mix y concentración: cuánto pesa cada afianzadora este año contra el pasado, qué parte del año depende de tus cinco y diez clientes más grandes, y cuántos clientes hacen el 80%.',
      'Cobro de la comisión: lo cobrado, lo que falta y el adeudo de cada año, más los días promedio que tarda en cobrarse. Los importes son el saldo de hoy, porque lo del año pasado ya está cobrado por el tiempo que pasó; los días son el renglón que sí compara un año contra otro.',
      'Las fianzas que no tienen un porcentaje de comisión que aplicar se señalan con un aviso: suman cero, y sin decirlo la comisión del año se leería baja sin explicación.',
    ],
  },
  {
    title: 'Nueva navegación de Reportes',
    date: '15 Sep 2026',
    tags: ['mejora', 'ux'],
    summary:
      'La parrilla de tarjetas que había arriba de cada reporte se convirtió en una lista lateral. Los reportes se ven todos de un vistazo, agrupados por tema, y el reporte deja de empezar abajo del fold.',
    bullets: [
      'La lista lateral se queda fija mientras lees: sólo scrollea el reporte, no la navegación.',
      'El selector de ejercicio y el botón de Exportar PDF viven en esa columna y siempre están a la vista, sin importar qué tan largo sea el reporte.',
      'La lista se puede esconder con un botón para darle todo el ancho a los reportes de tablas grandes. Surelia recuerda tu preferencia para la próxima vez que entres.',
      'Las pestañas General y Convenios pasaron a la misma columna, cada una con el número de reportes que contiene.',
      'En celular la lista se colapsa detrás del nombre del reporte activo.',
    ],
  },
  {
    title: 'Fiado y beneficiario en las tarjetas de Pulso',
    date: '15 Sep 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Las tarjetas del pipeline muestran quién tiene que emitir la fianza y a favor de quién. En convenios, el convenio, el fiado y el beneficiario venían en un solo renglón y se cortaba todo menos el convenio.',
    bullets: [
      'En las fianzas de convenio, el nombre del convenio pasó a la cinta azul de la tarjeta, y debajo quedan el fiado y el beneficiario en renglones propios.',
      'El fiado va primero: es el proveedor que tiene que emitir, el dato con el que trabajas.',
      'En las fianzas de cliente ahora también aparece el beneficiario, tomado del XML de la fianza.',
      'En las fianzas requeridas que todavía no se emiten no hay XML, así que no se muestra beneficiario en lugar de dejar el renglón vacío.',
      'El buscador de Pulso encuentra por convenio, fiado y beneficiario; antes, al ir todo concatenado, buscar un fiado no devolvía nada.',
    ],
  },
  {
    title: 'Buscador en Pulso',
    date: '15 Sep 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Pulso tiene un buscador arriba de las pestañas: conforme escribes van desapareciendo los renglones que no coinciden.',
    bullets: [
      'Filtra la pestaña en la que estés parado —Clientes, Pipeline, Diferidas, Después del Término, Vencimientos y Endosos Pendientes.',
      'Busca por cliente, convenio, contrato, número de fianza, tipo, afianzadora, beneficiario, proyecto y RFC.',
      'No distingue mayúsculas ni acentos: "bmm" encuentra "BMM" y "construccion" encuentra "Construcción".',
      'Se combina con los filtros que ya usabas —origen, antigüedad, renovables— en lugar de reemplazarlos.',
    ],
  },
  {
    title: 'Correcciones en los reportes de convenios',
    date: '15 Sep 2026',
    tags: ['bug', 'ux'],
    summary:
      'Arreglos en los reportes de convenios de proveedores a partir de lo que se detectó en uso.',
    bullets: [
      'Comisión por convenio: al expandir un renglón, las fianzas se veían amontonadas, un dato por línea. Ahora quedan en columnas alineadas y con encabezado, y los nombres largos de proveedor o afianzadora se recortan con puntos suspensivos en lugar de romper el renglón.',
      'Cumplimiento por convenio: al hacer clic en un proveedor ahora se abre su tarjeta directamente, en vez de sólo llevarte a la pestaña de Proveedores del convenio.',
      'Cumplimiento por convenio: la flecha de regresar del convenio ahora vuelve al reporte de donde saliste, no al listado de convenios.',
      'Los importes largos, como el monto expuesto, ya no se salen de su recuadro.',
      'Proveedores en riesgo: el buscador se movió arriba para dejarle el ancho completo a los indicadores.',
    ],
  },
  {
    title: 'Número de inclusión en las pólizas que lo manejan',
    date: '9 Sep 2026',
    tags: ['mejora'],
    summary:
      'Algunas afianzadoras emiten inclusiones sobre una misma póliza. Cuando el XML de la fianza trae numeroInclusion, Surelia ahora lo lee, lo guarda y lo muestra junto al número de fianza en toda la plataforma.',
    bullets: [
      'En todas las tablas de fianzas —CRM, reportes, convenios y portal del cliente— el número se muestra como número-inclusión (por ejemplo 1234567-25) cuando la póliza tiene inclusión.',
      'En el detalle de la fianza aparece como etiqueta junto al número y como campo propio en los datos del XML.',
      'El correo de fianza emitida incluye la fila "Número de inclusión" cuando existe.',
      'Aplica igual en fianzas de cliente y de convenio de proveedores; un endoso que cambie la inclusión queda registrado en el historial como cambio de datos.',
      'Un valor de 0 en el XML significa póliza base sin inclusión y no se muestra.',
      'Las fianzas subidas antes de este cambio lo adquieren al reemplazar su XML desde el detalle de la fianza.',
    ],
  },
  {
    title: 'Correos al fiado: solicitudes, recordatorios y fianzas emitidas',
    date: '8 Sep 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Surelia ahora envía correos con tu marca directamente al fiado: la solicitud de documentos al crear un link, recordatorios de lo que falta, y la fianza emitida con sus documentos adjuntos. Todo queda registrado en la bitácora y en un historial de correos por cliente.',
    bullets: [
      'Al crear un link de expediente, el correo al contacto del cliente sale activado por defecto, con el destinatario editable y un mensaje opcional (por ejemplo "Solicitamos actualización anual"); puedes desactivarlo y compartir el link a mano como siempre.',
      'Los links con documentos pendientes tienen un botón de recordatorio: el correo lista solo los documentos que faltan. Hay un límite de un correo por link cada 4 horas para no saturar al fiado.',
      'Al subir una fianza se ofrece enviarla por correo al contacto del cliente y a sus cuentas del portal, todos preseleccionados y deseleccionables; también puedes escribir otros correos a mano.',
      'El correo de la fianza incluye número, tipo, ramo y subramo, fiado, beneficiario, afianzadora, monto afianzado, vigencia y número de contrato, y adjunta los PDFs y XMLs de la emisión y de cada endoso, cada archivo con su casilla.',
      'Para reenviar una fianza que el fiado perdió: botón "Enviar por correo" en el detalle de la fianza, y accesos con icono de sobre en la tabla de fianzas y en el detalle del contrato. Cada fianza puede enviarse hasta 3 veces por hora, para evitar envíos accidentales.',
      'Los correos usan tu color primario, tu acento y tu logo (Configuración → Marca) y cierran con el sello "Powered by Surelia"; si el fiado responde, la respuesta llega al correo del usuario que lo envió.',
      'Cada envío queda en la bitácora, como pills debajo de cada link, y en la nueva columna "Correos" de la pestaña Bitácora del cliente, con destinatario, asunto, fecha y estado.',
      'Configuración → Uso muestra cuántos correos ha enviado tu organización en los últimos 6 meses; haz clic en un mes para ver cada correo con su tipo, destinatario, fecha y estado.',
    ],
  },
  {
    title: 'Bandeja de entrada en el portal del cliente',
    date: '8 Sep 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Los usuarios del portal tienen una nueva sección "Bandeja" con los correos que tu despacho les ha enviado sobre sus empresas: fianzas emitidas, solicitudes de documentos y recordatorios, del más reciente al más antiguo.',
    bullets: [
      'Los correos posteriores a su última visita se marcan como "Nuevo", y el menú lateral muestra un punto verde cuando hay algo sin ver.',
      'Las fianzas emitidas se abren directo desde la bandeja, sin buscarlas en las tablas.',
    ],
  },
  {
    title: 'Avisos cuando los datos no coinciden con el cliente',
    date: '7 Sep 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Surelia ahora compara la razón social y el RFC que registraste en el cliente contra lo que se lee de sus documentos, contratos y fianzas, y te avisa con una nota ámbar cuando hay una contradicción. Los avisos nunca bloquean nada: son señales para revisar.',
    bullets: [
      'En la página del cliente aparece un panel "Datos que no coinciden" cuando la constancia de situación fiscal, el acta constitutiva, las declaraciones u otros documentos del expediente traen una razón social o RFC distintos a los guardados.',
      'Al ver los datos extraídos de un documento, el campo que no coincide se marca en ámbar con el valor guardado del cliente para compararlo ahí mismo; si corriges el dato, el aviso desaparece.',
      'En los contratos, se avisa cuando el proveedor leído del contrato no coincide con el nombre del cliente.',
      'Al subir una fianza, se valida que el nombre y el RFC del fiado del XML (y del receptor de la factura) coincidan con el cliente; el aviso aparece al subir y queda visible en el detalle de la fianza.',
      'La comparación de nombres ignora acentos, puntuación y sufijos legales (S.A. de C.V., etc.), y la de RFC ignora mayúsculas y guiones, para no marcar diferencias de formato como errores.',
      'El RFC del cliente ahora se guarda en mayúsculas y sin espacios automáticamente, y sus cambios se registran en la bitácora.',
    ],
  },
  {
    title: 'Correos de bienvenida y avisos de acceso con tu marca',
    date: '7 Sep 2026',
    tags: ['mejora', 'ux', 'configuración'],
    summary:
      'Al dar acceso a un usuario cliente, Surelia puede enviarle un correo de bienvenida con el enlace para establecer su contraseña, y le avisa por correo cuando le asignas un nuevo fiado o convenio. Los correos usan los colores y el logo de tu marca.',
    bullets: [
      'Al crear un usuario desde "Dar Acceso" o "Invitar Usuario Cliente" decides con una casilla si se le envía el correo de bienvenida; el enlace para establecer contraseña se sigue mostrando para compartirlo manualmente.',
      'Cuando a un usuario cliente existente le asignas un nuevo fiado o un convenio, recibe un correo con los accesos nuevos. Quitar accesos no genera ningún aviso.',
      'Los correos usan tu color primario, tu color de acento y tu logo, tal como los tengas en Configuración → Marca. Sin personalización se envían con la marca Surelia.',
      'Nueva vista previa "Correo" en Configuración → Marca para ver cómo les llega el correo con tu marca antes de guardar.',
      'Mensajes de error más claros al invitar: si el correo ya tiene una cuenta, el diálogo ahora lo dice en lugar de mostrar un error genérico.',
      'Nueva sección "Correos enviados" en Configuración → Organización: historial de los correos enviados a tus usuarios con fecha, destinatario, tipo, estado y, en los avisos de acceso, a qué fiados o convenios se dio acceso. La sección está plegada por defecto y carga de 25 en 25.',
    ],
  },
  {
    title: 'Nuevo portal para tus clientes fiados',
    date: '4 Sep 2026',
    tags: ['mejora', 'ux'],
    summary:
      'La vista que ve un cliente fiado en el portal se rediseñó por completo, al mismo nivel que la de convenios: una sola página de su empresa con pestañas, en lugar de páginas sueltas de expedientes, contratos, fianzas y afianzadoras.',
    bullets: [
      'Página de Empresa con pestañas Resumen, Contratos, Fianzas, Expediente y Afianzadoras. El menú lateral del cliente queda en Inicio, Empresa y Reportes; los links anteriores siguen funcionando y llevan a la pestaña correspondiente.',
      'Resumen con la salud de sus fianzas (monto afianzado, vencidas, por emitir, atrasadas), el avance de su expediente y los próximos vencimientos a 30, 60 y 90 días.',
      'Contratos con filas expandibles que muestran las fianzas emitidas y los requisitos por emitir de cada contrato, igual que en el CRM.',
      'Fianzas con búsqueda y filtros por estatus, pago y vigencia, más la tabla de requisitos por emitir y futuras. El cliente ve el estado de pago de sus fianzas; primas, endosos y comisiones no aparecen en el portal.',
      'Reportes del cliente fiado renovados: Estadísticas, Desglose por contrato, Vencimientos y Por emitir, todos con descarga en CSV. Se corrigió además el conteo de "por emitir", que antes podía contar de más y marcaba como pendientes las fianzas futuras (vicios ocultos).',
    ],
  },
  {
    title: 'Nuevo portal para los usuarios de convenio',
    date: '4 Sep 2026',
    tags: ['mejora', 'ux'],
    summary:
      'La vista de convenio que ven los usuarios de tus beneficiarios se rediseñó para reflejar la página de convenios del CRM, con la información limitada a lo que le corresponde ver al beneficiario.',
    bullets: [
      'Página del convenio con pestañas Resumen, Contratos, Fianzas, Proyectos y Proveedores; los contratos se expanden para ver sus fianzas y requisitos.',
      'Selector de contexto en el menú lateral: una cuenta con acceso a varias empresas o convenios elige cuál está viendo, y toda la navegación se ajusta a esa selección.',
      'Reportes de convenio ampliados de 2 a 5: Estadísticas, Desglose, Vencimientos, Por emitir y Proveedores, con descarga en CSV y apertura de fianzas y contratos sin salir del reporte.',
      'El beneficiario nunca ve primas, estados de pago, endosos ni comisiones: esa información comercial es del fiado y de tu despacho.',
    ],
  },
  {
    title: 'Los convenios se estiman con una sola Tabla general',
    date: '3 Sep 2026',
    tags: ['mejora', 'comisiones', 'configuración'],
    summary:
      'La sección de Convenios en Configuración → Comisiones se simplifica a una Tabla general. Antes tenías que capturar un tarifario por afianzadora; si no lo hacías, la comisión del agente del fiado se estimaba con tus propios porcentajes.',
    bullets: [
      'Nuevo botón "Crear Tabla general" en la sección de Convenios. Es una sola tabla y estima la comisión de todas las fianzas de convenio, sin importar la afianzadora.',
      'Ya no se capturan tarifarios por afianzadora en Convenios. Los que ya tenías se siguen usando y tienen prioridad sobre la Tabla general.',
      'La Tabla general de convenios sustituye el uso de tus propias tablas como estimación por defecto en las fianzas de convenio.',
      'Estas comisiones siguen marcadas como "por confirmar": el porcentaje real es el del agente del fiado y se confirma cuando lo muestra la fianza emitida.',
      'Al crear la Tabla general de convenios, Surelia te propone retarificar las comisiones pendientes que se habían estimado con tus propias tablas.',
      'El reparto beneficiario/fiado por afianzadora no cambia: se sigue configurando en Participación de convenios.',
    ],
  },
  {
    title: 'Número de contrato recuperado en los contratos existentes',
    date: '3 Sep 2026',
    tags: ['bug', 'mejora'],
    summary:
      'Los contratos que subías desde el expediente de un cliente perdían el número de contrato: Surelia sí lo leía del documento, pero no lo guardaba. Ya quedó corregido y recuperamos el número de todos los contratos que estaban sin él.',
    bullets: [
      'El número de contrato y el proveedor ya se guardan al procesar un contrato subido desde el expediente de un cliente. Los contratos de convenio nunca tuvieron este problema.',
      'Recuperamos el número de contrato de los contratos que ya estaban en la plataforma, a partir de la lectura original del documento. No hizo falta volver a procesar nada.',
      'Si habías capturado o corregido un número a mano, se respetó tal cual.',
      'El número ya aparece en las tablas de Contratos y de Fianzas, en la pestaña de Contratos y fianzas de cada cliente, y puedes buscar y ordenar por él.',
    ],
  },
  {
    title: 'Fianzas por emitir y columnas congeladas en las tablas',
    date: '31 Ago 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Fianzas suma un panel "Por emitir" con los requisitos que declaran tus contratos y que todavía no tienen fianza emitida. Además, las tablas principales de la plataforma dejan fijas sus primeras columnas al desplazarte a lo ancho.',
    bullets: [
      'Nuevo panel "Por emitir" en Fianzas, con el tipo de fianza, número y título del contrato, porcentaje, monto por afianzar y fecha de emisión de cada requisito.',
      'Cada requisito se marca como Atrasada, Por emitir o Futura según su momento de emisión.',
      'El orden por defecto es por urgencia: primero las atrasadas, luego por fecha de emisión y al final las futuras. También puedes ordenar por cliente o por monto.',
      'Al hacer clic en un requisito se abre el contrato que lo declara.',
      'El buscador del panel filtra por cliente, tipo de fianza, número y título del contrato. Los filtros de estatus y de pago quedan desactivados mientras el panel está activo.',
      'Columnas congeladas en las tablas de Clientes, Contratos y Fianzas, y en las de contratos y fianzas dentro de cada cliente.',
    ],
  },
  {
    title: 'Convenios: reparto de comisión con el otro agente y seguimiento de proveedores',
    date: '27 Ago 2026',
    tags: ['mejora', 'comisiones', 'configuración'],
    summary:
      'Surelia ya calcula el reparto de comisión de las fianzas de convenio, lo registra en cada movimiento y te muestra tu parte neta. Además, el módulo de Convenios se rediseñó alrededor de qué te debe cada proveedor hoy.',
    bullets: [
      'La base del cálculo es siempre el porcentaje del agente del fiado; sobre esa comisión bruta se aplica el reparto. En el movimiento quedan guardados la comisión base, de qué lado del convenio estás y con qué porcentaje, para que el monto que ves sea tu neto real.',
      'Nueva sección "Participación de convenios" en Configuración > Comisiones: define por afianzadora el porcentaje de Convenio (beneficiario) y el de Fiado. Si no configuras una afianzadora se aplica 70 / 30.',
      'Las tablas de comisiones ahora se separan en "Mis tablas de comisiones" y "Tarifarios de convenios". Las de convenio se aplican primero al tarificar una fianza de convenio.',
      'Reparto distinto en una fianza puntual: desde el editor de comisión puedes fijar la participación de esa fianza sola. El ajuste manual gana sobre la configuración de la organización.',
      'Cuando el porcentaje viene de un tarifario de convenio por defecto, la comisión se marca como estimada hasta que confirmes el porcentaje real.',
      'Lista de seguimiento de proveedores con filtros por lo que te deben hoy: Con pendientes, Por emitir, Atrasadas, Futuras, Vencidas y Al corriente.',
    ],
  },
  {
    title: 'Ledger de comisiones: cada peso trazable hasta su fianza',
    date: '26 Ago 2026',
    tags: ['mejora', 'comisiones'],
    summary:
      'Las comisiones dejan de calcularse al vuelo en cada reporte y pasan a registrarse como movimientos. Cada movimiento guarda el momento en que ocurrió, cómo se llegó al porcentaje y en qué estado de cobro está, y se puede rastrear peso por peso hasta la factura, la fianza, la afianzadora y el cliente.',
    bullets: [
      'Nueva sección "Comisiones" en el menú principal, para propietarios: todos los movimientos de la organización con columna de Cliente, búsqueda y paginación. Cada cliente tiene además su propia vista de comisiones.',
      'Estados de cobro: Pendiente, Cobrada, Neutralizada, Adeudo y Adeudo liquidado. Una comisión no puede marcarse como cobrada hasta que la factura de la prima esté pagada.',
      'Panel de comisiones dentro de cada fianza con el detalle de los movimientos y su historial de cambios.',
      'Editor de comisión: corrige en un solo lugar el porcentaje del agente del fiado y el reparto con el otro agente.',
      'Retarificación: al cambiar un tarifario, Surelia te lista las fianzas con comisión pendiente que se calcularon con la tabla anterior y te deja elegir cuáles actualizar. Las comisiones ya cobradas y las que ajustaste a mano nunca se tocan.',
      'Saldos por cliente: saldo en contra (primas facturadas y no pagadas) y saldo a favor (dinero que el cliente ya pagó y que una reexpedición menor ya no cubre). Se muestran por separado y nunca se netean en un solo número.',
      'Siete reportes nuevos: Liquidación de comisiones, Cobranza de primas, Saldos de clientes, Metas por afianzadora, Ciclo de vida de fianzas, Beneficiarios y Estadísticas de la cartera.',
      'Metas de prima por afianzadora por año, capturables desde el cliente y visibles en el reporte de metas.',
      'Inicio con tres cifras de dinero: Cobrable ahora, Por exigir y Adeudo con afianzadoras.',
      'Todos los movimientos se escriben desde el servidor. La bitácora de comisiones es de solo lectura para el propietario y está cerrada para el resto de los usuarios.',
    ],
  },
  {
    title: 'Endosos, renovaciones y ciclo de vida de la fianza',
    date: '26 Ago 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Una fianza deja de ser un registro que se sobrescribe y pasa a tener historia: endosos, cancelaciones, anulaciones, sustituciones y renovaciones quedan registrados como eventos, con su documentación y su efecto en la prima.',
    bullets: [
      'Registro de endosos con siete tipos: cambio de texto, aumento, disminución, cambio de vigencia, aumento o disminución de vigencia, cambio de datos, y cambio de fiado o beneficiario. Un mismo endoso puede ser de varios tipos a la vez.',
      'El efecto en la prima se captura aparte del tipo: sin cambio, factura adicional o factura que sustituye a la anterior.',
      'Cada fianza lleva su propio registro de facturas (emisión, adicional y sustituye), con el pagado marcado factura por factura y no sobre la fianza completa.',
      'Estados de la fianza: activa, cancelada, anulada y sustituida. Sustituir una fianza crea la nueva y deja la anterior marcada como sustituida, ligadas entre sí.',
      'Renovación: genera la fianza nueva enlazada a la anterior, que conserva su estado y su comisión.',
      'Solicitud de endosos: registra un endoso pedido y todavía no aplicado, con quién lo pidió (cliente, beneficiario, afianzadora u otro), fecha límite y estatus Solicitado, En proceso, Aplicado o Cancelado.',
      'Los endosos pendientes aparecen en Pulso en su propia sección, resaltando los vencidos y los urgentes.',
      'Documentos del contrato: acta de entrega, convenio modificatorio, anulación y otros. Adjuntar el acta de entrega o el documento de anulación aplica el evento correspondiente sobre la fianza.',
      'Línea de tiempo del contrato e historial por fianza con todos sus eventos en orden.',
      'La tabla de Fianzas incorpora los nuevos estados: Terminadas, Endoso pendiente, Renueva anterior y Sustituye anterior, además de filtros por estatus, por pagada o sin pagar, y por vencimiento a 30 días.',
    ],
  },
  {
    title: 'Controla si tus clientes ven la tarifa en el portal',
    date: '12 Ago 2026',
    tags: ['mejora', 'configuración'],
    summary:
      'Nueva sección "Portal de Clientes" en Configuración > Organización (para propietarios): decide si los usuarios cliente ven la tarifa de las fianzas.',
    bullets: [
      'Aplica a toda la organización: usuarios de clientes y de convenios por igual.',
      'El cambio se refleja en vivo en el portal, sin que tus clientes recarguen la página.',
      'Por defecto la tarifa sigue visible; nada cambia si no tocas el switch.',
    ],
  },
  {
    title: 'Mejoras de marca: fondo del logo, recepción documental y pestaña del navegador',
    date: '12 Ago 2026',
    tags: ['mejora', 'configuración', 'ux'],
    summary:
      'La personalización de marca ahora se ve bien con cualquier logo y cualquier color.',
    bullets: [
      '¿Logo oscuro? Elige "fondo blanco" en Configuración > Marca y aparecerá sobre un recuadro blanco en la barra lateral.',
      'La página de recepción de documentos usa el mismo degradado de dos colores que el inicio de sesión.',
      'Los colores de las letras en la recepción se calculan automáticamente a partir de tu marca para que siempre se lean bien, incluso con colores claros.',
      'El título de la pestaña del navegador en la recepción usa tu nombre comercial.',
      'Nuevo enlace recomendado para quitar el fondo de tu logo: remove.bg.',
    ],
  },
  {
    title: 'Personalización de marca: tu logo y tus colores en Surelia',
    date: '3 Ago 2026',
    tags: ['mejora', 'configuración', 'ux'],
    summary:
      'Nueva pestaña "Marca" en Configuración (para propietarios): sube tu logo y elige tus colores, con vista previa en vivo.',
    bullets: [
      'Tu logo aparece en el panel y en la página donde tus clientes suben documentos, con o sin subdominio.',
      'El inicio de sesión con tu marca solo funciona con un subdominio propio (tu-empresa.surelia.mx); en app.surelia.mx se muestra la marca Surelia. Pídelo con "Solicitar subdominio".',
      'Recomendamos un PNG sin fondo; incluimos un enlace para quitarlo gratis.',
      '"Quitar logo" y "Restablecer a Surelia" para volver al aspecto original.',
      'Disponible para organizaciones habilitadas. Si no ves la pestaña, contáctanos.',
    ],
  },
  {
    title: 'Estados Financieros Parciales en el expediente y comparador de razones financieras',
    date: '27 Jul 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nuevo tipo de documento "Estados Financieros Parciales (Contador)" para recibir estados financieros del periodo en curso, además de los anuales. Se extrae automáticamente el periodo que cubren y las Razones Financieras permiten alternar entre los parciales y los anuales.',
    bullets: [
      'Disponible al solicitar documentos: aparece como opción en la creación de links, dentro del paquete "Actualización" y en "Todos".',
      'La extracción captura las cifras del Balance General y Estado de Resultados igual que los estados anuales, y adicionalmente el periodo que cubren (fecha de inicio y fecha de corte).',
      'Razones Financieras: nuevo selector Parcial / Anual que cambia la fuente de los indicadores. Se muestra el periodo de los estados usados y, por defecto, se toma el más reciente.',
      'Seguimiento de vigencia: los estados parciales con más de 3 meses de antigüedad se marcan en "Documentos que requieren atención".',
      'Visible también en el portal del cliente y en el historial de documentos por link.',
    ],
  },
  {
    title: 'Mi Equipo: alta corregida, correo de bienvenida y tope de 100 miembros',
    date: '29 Jun 2026',
    tags: ['bug', 'mejora', 'configuración'],
    summary:
      'Se corrigió el alta de miembros del equipo (propietarios y operadores), que dejaba al nuevo usuario sin acceso a la organización. Ahora cada nuevo miembro recibe automáticamente un correo de bienvenida. No afecta a cuentas de cliente ni a accesos a convenios.',
    bullets: [
      'El nuevo miembro aparece de inmediato en Mi Equipo y puede iniciar sesión con acceso completo a la organización.',
      'Correo de bienvenida automático con un botón "Establecer contraseña". Si no llega, el nuevo usuario puede restablecer su contraseña desde la pantalla de inicio de sesión con el correo dado de alta.',
      'Tope de 100 miembros por organización: al llegar al límite el botón "Crear Usuario" se desactiva. Contáctanos si necesitas ampliarlo. Se eliminó el contador "X / Y" basado en el plan.',
    ],
  },
  {
    title: 'Preferencias de correo en Configuración',
    date: '6 Jun 2026',
    tags: ['mejora', 'configuración'],
    summary:
      'Nueva pestaña "Correo" en Configuración para que cada usuario decida qué tipo de correos quiere recibir de Surelia. Las listas operativas (Facturación y Seguridad) siempre permanecen activas porque son necesarias para mantener tu cuenta en orden.',
    bullets: [
      'Interruptores independientes por tipo de correo: Novedades del producto, Notas de versión, Tips y consejos, y Recordatorios de cumplimiento.',
      'Pausa global "Pausar todos los correos no transaccionales" para silenciar de un solo clic todas las listas opcionales sin tener que tocar cada una.',
      'Facturación y Seguridad se muestran como "siempre activas" — los necesitamos para enviarte recibos, facturas y alertas de acceso.',
      'Los cambios se guardan automáticamente conforme alternas los interruptores; no hay botón de Guardar.',
      'Disponible para todos los roles (administradores, operadores y cuentas de cliente).',
    ],
  },
  {
    title: 'Bitácora completa en el Historial del convenio',
    date: '28 May 2026',
    tags: ['bug', 'mejora'],
    summary:
      'Acciones sobre fianzas y contratos de un convenio ahora aparecen en el Historial del convenio. Antes se guardaban en la base de datos pero quedaban invisibles porque les faltaba el identificador del convenio en su metadata.',
    bullets: [
      'Subida de una nueva fianza emitida en un contrato del convenio ahora aparece en el Historial, identificando al proveedor en lugar de "Sin nombre".',
      'Edición de fianzas requeridas de un contrato (agregar, eliminar, actualizar tipo / porcentaje / monto / estatus) queda registrada con el convenio, beneficiario y proveedor correctos.',
      'Reprocesamiento de contrato y aceptación del documento procesado tras revisar todos los campos también quedan visibles en el Historial.',
      'Acciones sobre una fianza ya emitida (reemplazo de XML o PDF, marcar como pagada o renovable, eliminación) ahora se registran con el contexto del convenio.',
      'Las acciones equivalentes sobre contratos tradicionales de cliente siguen registrándose como antes; solo cambia el enrutamiento cuando el contrato pertenece a un convenio.',
    ],
  },
  {
    title: 'Convenios: número de contrato en tablas y captura de monto en pesos',
    date: '28 May 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Las tablas de convenios ahora muestran el número de contrato en lugar del nombre, y la edición de fianzas requeridas de un contrato captura el monto con formato de pesos mexicanos. Se corrigieron varias molestias al editar.',
    bullets: [
      'Tablas del convenio (pestañas Resumen, Contratos, Fianzas y Proveedores) y del portal del cliente muestran el número de contrato en la columna correspondiente.',
      'La búsqueda en la lista de proveedores ahora también filtra por número de contrato, además de nombre, proyecto y tipo.',
      'Pestaña de Contratos del convenio: se eliminó el botón duplicado "Nuevo Contrato" que aparecía sobre la tabla; se mantiene el del encabezado del convenio.',
      'Edición de fianzas requeridas: el campo Monto se muestra con separadores de miles y prefijo "$" / sufijo "MXN", admite hasta 2 decimales y se vacía al limpiar — ya no aparece un "0" inicial que ensucia lo que tecleas.',
      'Cancelar la edición de fianzas requeridas ahora revierte realmente los cambios: antes los valores nuevos quedaban visibles en el panel lateral aunque no se hubiera guardado.',
    ],
  },
  {
    title: 'Expediente del cliente: tablas de Excel y estado por link',
    date: '28 May 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Mejoras al detalle del cliente: las tablas financieras del Excel de Estados Financieros muestran los años correctos en expedientes recientes e históricos, y el historial de links de solicitud indica de un vistazo qué documentos están entregados, en proceso, con error o pendientes en cada link.',
    bullets: [
      'Tablas financieras del Excel (Balance General, Estado de Resultados y Conciliación Fiscal) muestran los años reales del archivo, tanto en expedientes actuales como en los históricos accesibles desde "Ver historial".',
      'La vista de Ver resultados para un Excel aprovecha todo el ancho de la pantalla.',
      'Historial de links de solicitud: nueva fila bajo cada link con una etiqueta por documento requerido, marcada como Entregado, En proceso, Error o Pendiente.',
      'Contador "X/Y entregados" junto al nombre del link, y tooltip por etiqueta con el nombre completo del documento.',
      'El indicador de estado de cada link (verde / naranja) ahora refleja la realidad: verde solo cuando todos los documentos requeridos están entregados.',
    ],
  },
  {
    title: 'Detección de fiados que no coinciden con el contrato',
    date: '14 May 2026',
    tags: ['mejora', 'ux'],
    summary:
      'En convenios, el nombre del proveedor del contrato es la fuente de verdad. Cuando el fiado del XML de la fianza —o el receptor de la factura— no coincide con ese proveedor, la plataforma lo señala en cada punto donde la fianza aparece, sin alterar los datos oficiales del XML.',
    bullets: [
      'Aviso prominente al subir una fianza cuando el fiado del XML o el receptor de la factura difieren del proveedor registrado en el contrato.',
      'Comparación por nombre con normalización inteligente: ignora acentos, mayúsculas, puntuación y sufijos legales (S.A. de C.V., S. de R.L., SAPI, SAB, SC, AC) para evitar falsas alarmas por variaciones de escritura.',
      'Indicadores de inconsistencia visibles en el detalle de la fianza, en las tarjetas de proveedor del convenio, en la tabla de fianzas emitidas del portal y en el detalle del contrato.',
      'Avisos cerrables en el diálogo de carga para no obstruir la revisión de los XMLs una vez identificados.',
      'El dato del contrato prevalece como fuente de verdad; los XMLs oficiales no se modifican — solo se señala la divergencia para que el equipo decida si corregir el proveedor del contrato o aceptar la fianza tal cual.',
    ],
  },
  {
    title: 'Tablero de Convenios y eliminación de fianzas',
    date: '14 May 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Rediseño del inicio del portal y del resumen del convenio para que los beneficiarios vean en segundos su exposición, vencimientos próximos y proveedores que requieren atención. Se habilita la eliminación de fianzas con registro completo en bitácora y se prioriza la prominencia de las advertencias al subir XMLs.',
    bullets: [
      'Nuevo tablero de inicio para usuarios de convenios: cuatro paneles con salud del convenio (afianzado vs sin afianzar vs vencido), próximos vencimientos a 30/60/90 días, distribución por afianzadora y proveedores que requieren atención inmediata.',
      'Resumen del convenio con selector de beneficiario: en convenios con múltiples beneficiarios, el resumen puede filtrarse para ver los datos de uno solo o de todos en conjunto.',
      'Tabla de contratos del convenio: ahora muestra el número de contrato en lugar del título y agrega columna de Beneficiario, con filtros desplegables por proveedor y beneficiario más búsqueda por número.',
      'Tabla de fianzas del convenio: muestra el número de contrato y al hacer clic abre directamente el detalle de la fianza en lugar del contrato.',
      'Eliminación de fianzas: nuevos botones para eliminar fianzas desde su detalle. Se eliminan los archivos asociados, se desvincula la fianza requerida correspondiente y queda registrado en bitácora.',
      'Aviso prominente al subir fianzas con datos inconsistentes: las advertencias o errores críticos detectados al validar los XMLs se muestran ahora en un banner de alta visibilidad al inicio del diálogo de carga.',
      'Reportes del portal: se removieron tarjetas redundantes con el nuevo tablero; los reportes ahora ofrecen búsqueda y descarga en CSV del desglose por convenio y del listado completo de proveedores.',
      'Bitácora más confiable: se corrigió una falla silenciosa que impedía registrar acciones cuando algún campo opcional faltaba, y los registros relacionados con convenios ahora muestran el nombre del convenio (y beneficiario, cuando aplica) en lugar del cliente.',
      'Reglas de almacenamiento unificadas entre entornos: producción, test y local ahora aplican las mismas reglas de acceso a archivos de convenios.',
    ],
  },
  {
    title: 'Endurecimiento y refinamiento del módulo de Convenios',
    date: '13 May 2026',
    tags: ['mejora', 'bug'],
    summary:
      'Cierre de brechas de aislamiento por proyecto en Convenios y ajustes de claridad en las vistas que comparten los beneficiarios. Garantiza que un usuario con acceso restringido a un proyecto no vea información de otros proyectos del mismo convenio, y que los tableros reflejen su alcance real.',
    bullets: [
      'Aislamiento por proyecto reforzado: los archivos y resultados de OCR de un convenio ahora respetan estrictamente los proyectos asignados al usuario. Antes, un usuario con acceso parcial podía leer contenido de proyectos fuera de su alcance.',
      'Vista de detalle de fianza en convenios: el bloque de información ahora muestra "Beneficiario y Fiado" — con beneficiario, RFC, fiado, convenio y proyecto — en lugar de la etiqueta genérica de "Cliente" que aplica solo a contratos tradicionales.',
      'Tarjetas de convenios en el inicio del cliente: cada convenio muestra ahora su propio conteo de contratos y fianzas vencidas, no el total agregado de todos los convenios del usuario.',
      'Reportes del portal: el reporte seleccionado se ajusta automáticamente cuando los permisos terminan de cargar, evitando pantallas en blanco para usuarios que solo tienen acceso a convenios.',
      'Listado de fianzas del portal: refleja en tiempo real los cambios de acceso al convenio sin necesidad de recargar.',
    ],
  },
  {
    title: 'Convenios de Proveedores',
    date: '13 May 2026',
    tags: ['mejora'],
    summary:
      'Nuevo módulo para administrar convenios marco con tus clientes beneficiarios y todos los proveedores que operan bajo ellos. Carga contratos por convenio, vincula proyectos y otorga acceso de solo lectura a tus contactos del beneficiario para que consulten el estado de sus fianzas sin pasar por ti.',
    bullets: [
      'Nuevo módulo "Convenios" en el menú principal con listado, búsqueda y creación de convenios.',
      'Cada convenio agrupa beneficiarios, proyectos y contratos relacionados bajo un mismo acuerdo marco.',
      'Carga de contratos directamente desde el detalle del convenio con OCR automático: se extrae título, número, tipo, vigencia, monto, beneficiario, proveedor y fianzas requeridas.',
      'Distinción explícita entre Beneficiario (quien contrata el servicio) y Proveedor (quien lo presta) en la vista del contrato.',
      'Vista de Proveedores como pestaña principal del convenio: cada proveedor muestra fianzas pendientes, vigentes y vencidas con indicadores de riesgo (vencidas en rojo, por vencer en ámbar), monto total y monto vencido. Ordenadas por riesgo automáticamente.',
      'Banner de riesgo cuando hay fianzas vencidas de emisión, con monto total expuesto.',
      'Búsqueda en la vista de Proveedores por nombre, contrato, proyecto o tipo de fianza.',
      'Resumen del convenio rediseñado como dashboard: estadísticas globales y top 5 proveedores con mayor monto pendiente, con navegación directa al proveedor.',
      'Acción rápida "Subir fianza" desde cada fianza pendiente en la vista del corredor — abre el formulario de subida con el contrato y fianza prellenados.',
      'Notas internas del equipo por convenio, no visibles para los usuarios cliente.',
      'Acceso granular para usuarios cliente: completo a todo el convenio o restringido a proyectos específicos.',
      'Portal del cliente con la misma vista de Proveedores como pestaña principal — los beneficiarios ven inmediatamente qué proveedores tienen fianzas pendientes y cuáles están al corriente.',
      'Pulso: los contratos de convenios aparecen identificados con estilo diferenciado y filtro de origen (Clientes / Convenios / Todos).',
      'Para activar Convenios en tu organización, contáctanos.',
    ],
  },
  {
    title: 'Mejoras generales',
    date: '13 Mar 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Mejoras de experiencia de usuario y trazabilidad en la plataforma.',
    bullets: [
      'Pantalla de carga con logo al iniciar sesión o recargar la página — elimina el parpadeo entre vistas.',
      'Detección inmediata de nueva versión al volver a la pestaña de Surelia, sin esperar al ciclo de 5 minutos.',
      'Registro en bitácora de acciones de cuentas de cliente: creación, actualización de acceso y desactivación.',
    ],
  },
  {
    title: 'Cuentas de Cliente',
    date: '13 Mar 2026',
    tags: ['mejora'],
    summary:
      'Ahora puedes crear cuentas de acceso para tus clientes. Tus fiados podrán iniciar sesión en la plataforma y consultar en tiempo real el estado de sus fianzas, contratos, expedientes y líneas de afianzamiento — sin necesidad de llamarte o enviarte un correo para cada consulta.',
    bullets: [
      'Crea usuarios de tipo "cliente" desde la configuración de tu organización y asígnales las empresas que les corresponden.',
      'Tus clientes acceden con su propio correo y contraseña a un portal de solo lectura — no pueden modificar, subir ni eliminar nada.',
      'Pueden ver el detalle de cada fianza emitida, el estatus de cada contrato y qué fianzas están pendientes de emisión.',
      'Visibilidad completa de sus líneas de afianzamiento: línea autorizada, línea usada y disponible por cada afianzadora.',
      'Acceso al estado de su expediente con alertas de documentos vencidos o por vencer.',
      'Reduce la carga operativa de tu equipo: menos llamadas y correos de clientes preguntando "¿ya salió mi fianza?".',
    ],
  },
  {
    title: 'Portal del Cliente',
    date: '13 Mar 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Los clientes ahora pueden iniciar sesión en la plataforma y consultar directamente el estado de sus fianzas, contratos, expedientes y afianzadoras.',
    bullets: [
      'Inicio con resumen general: contratos, fianzas (vigentes/vencidas/por emitir), monto afianzado, alertas y resumen por afianzadora.',
      'Expedientes: estado de documentos por empresa, vigencia de cada documento y links para actualizar documentación.',
      'Contratos: consulta de todos los contratos con detalle, fianzas requeridas vs emitidas. Click para ver detalle del contrato.',
      'Fianzas: consulta de todas las fianzas emitidas con filtros por estatus, pago y empresa. Fianzas pendientes de emisión con acceso directo al contrato. Click para ver detalle de la fianza.',
      'Afianzadoras: línea de afianzamiento, línea usada y disponible, fianzas activas por afianzadora, obligados solidarios y bienes inmuebles en garantía.',
      'Reportes: resumen general, fianzas por tipo, distribución por estatus y estado de expedientes por empresa.',
      'Navegación completa por sidebar: Inicio, Expedientes, Contratos, Fianzas, Afianzadoras y Reportes.',
      'Vista de solo lectura: los clientes pueden consultar toda la información sin poder modificar datos.',
      'Compatible con dispositivos móviles.',
    ],
  },
  {
    title: 'Notificación de nueva versión disponible',
    date: '13 Mar 2026',
    tags: ['mejora', 'ux'],
    summary:
      'La plataforma ahora detecta automáticamente cuando hay una nueva versión desplegada y muestra una notificación para actualizar sin perder el contexto de trabajo.',
    bullets: [
      'Verificación automática cada 5 minutos en segundo plano.',
      'Banner flotante en la parte inferior de la pantalla cuando se detecta una actualización.',
      'Botón "Actualizar" que recarga la página para aplicar la nueva versión.',
    ],
  },
  {
    title: 'Visualización de Obligados Solidarios y Garantías Inmobiliarias en Contrato Múltiple',
    date: '12 Mar 2026',
    tags: ['bug', 'mejora'],
    summary:
      'Corrección en la vista de datos extraídos del Contrato Múltiple donde los Obligados Solidarios y las Garantías Inmobiliarias no se mostraban, apareciendo como "N/A" a pesar de existir en la base de datos.',
    bullets: [
      'Los Obligados Solidarios ahora se renderizan con nombre, domicilio y representante.',
      'Las Garantías Inmobiliarias ahora se renderizan con ubicación, valor comercial y datos RPP.',
      'Navegación de fuentes por cada elemento: botones para navegar a las líneas del PDF de donde se extrajo la información.',
      'Corrección del pintado de fuentes en el PDF: las líneas referenciadas por elementos anidados ahora se resaltan correctamente en el documento.',
    ],
  },
  {
    title: 'Archivar Clientes',
    date: '12 Mar 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nueva funcionalidad para archivar clientes que ya no están activos, ocultándolos junto con sus contratos y fianzas de todas las vistas principales sin eliminar datos.',
    bullets: [
      'Botón de archivar en cada fila de la tabla de Clientes con diálogo de confirmación.',
      'Los contratos y fianzas del cliente archivado se ocultan automáticamente de Contratos, Fianzas, Reportes, Pulso y Home.',
      'Nueva página "Clientes Archivados" accesible desde el botón "Archivados" en la tabla de Clientes.',
      'Vista expandible por cliente archivado mostrando sus contratos y fianzas asociados.',
      'Opción de "Desarchivar" para restaurar un cliente y sus datos a las vistas principales.',
      'Los clientes archivados se cargan bajo demanda, sin afectar el rendimiento de la carga inicial.',
      'Banner informativo al acceder al detalle de un cliente archivado.',
      'Registro completo en bitácora de acciones de archivar y desarchivar.',
    ],
  },
  {
    title: 'Pestaña de Afianzadoras en Detalle de Cliente',
    date: '12 Mar 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nueva pestaña "Afianzadoras" en el detalle de cliente para gestionar la relación del cliente con cada afianzadora: líneas de afianzamiento, bienes inmuebles, obligados solidarios y documentación por afianzadora.',
    bullets: [
      'Registro de afianzadoras por cliente con línea de afianzamiento y datos de contacto.',
      'Bienes Inmuebles vinculables a una o más afianzadoras, con filas clickeables y diálogo de detalle.',
      'Obligados Solidarios (persona física o moral) vinculables a afianzadoras, con documentación individual (Entrevista y Consulta Buró) por afianzadora.',
      'Vinculación de OS desde clientes existentes: al seleccionar un cliente registrado como OS, se muestra automáticamente su línea de afianzamiento, monto afianzado y disponible por cada afianzadora donde está registrado.',
      'Pills segmentados por afianzadora en cada OS mostrando el disponible de línea, facilitando la toma de decisiones sin salir de la vista.',
      'Cálculo automático de disponible: línea de afianzamiento menos suma de montos afianzados en fianzas activas, con indicador rojo cuando el disponible es negativo.',
    ],
  },
  {
    title: 'Alertas de vigencia de documentos',
    date: '12 Mar 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nuevo sistema de alertas que notifica cuando los documentos del expediente están vencidos o próximos a vencer, tanto en la vista del cliente como en la tabla de clientes.',
    bullets: [
      'Detección automática de vigencia para: Identificación oficial, Comprobante de domicilio, Constancia de situación fiscal, Acuse de recibo de declaración, Declaración del ejercicio y Estados financieros.',
      'Badges de "Vencido" (rojo) y "Por vencer" (ámbar) en las tarjetas de documentos procesados y en el Expediente Digital.',
      'Banner de "Documentos que requieren atención" en la pestaña de Información General con botón directo para solicitar actualización.',
      'Nueva columna "Docs" en la tabla de Clientes con indicador de semáforo: rojo (vencidos), ámbar (por vencer) o verde (OK).',
      'Reglas de vigencia adaptadas a regulación mexicana: comprobante de domicilio caduca a 3 meses, constancia fiscal a 1 año, ejercicios fiscales deben corresponder al año más reciente.',
    ],
  },
  {
    title: 'Corrección de descarga de documentos',
    date: '20 Feb 2026',
    tags: ['bug'],
    summary:
      'Corrección de un bug donde algunos archivos PDF se descargaban con extensión .json.',
    bullets: [
      'En ciertos casos, al descargar un documento desde el expediente, el archivo se guardaba como .json en lugar de .pdf.',
      'El contenido del archivo era correcto — al cambiar manualmente la extensión a .pdf funcionaba sin problema.',
      'Corregido para que siempre se use el nombre y extensión correctos del archivo original.',
    ],
  },
  {
    title: 'Perfil de usuario y Política de Privacidad',
    date: '6 Feb 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nueva pestaña de Perfil en Configuración y acceso a la Política de Privacidad desde el menú de usuario.',
    bullets: [
      'Nueva pestaña "Perfil" en Configuración mostrando nombre, correo, organización y rol.',
      'Política de Privacidad disponible desde el menú de usuario en un diálogo dentro de la plataforma.',
      'Página de Soporte Técnico con información de contacto por correo y WhatsApp.',
    ],
  },
  {
    title: 'Mejoras en Expediente Digital y Pulso',
    date: '5 Feb 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Correcciones en la visualización de documentos en el Expediente Digital y en la pestaña Clientes de Pulso.',
    bullets: [
      'Expediente Digital: eliminado el texto redundante "Pendiente de carga" que aparecía cuando no había documentos procesados.',
      'Pulso Clientes: corregido el bug que mostraba documentos no solicitados en gris al expandir un cliente.',
      'Ahora solo se muestran los documentos que fueron explícitamente marcados como requeridos en los links.',
    ],
  },
  {
    title: 'Nuevo reporte: Producción por Afianzadora',
    date: '3 Feb 2026',
    tags: ['mejora', 'comisiones'],
    summary:
      'Nuevo reporte que muestra la producción y comisiones agrupadas por afianzadora (emisor), con detalle interactivo.',
    bullets: [
      'Tabla resumen con fianzas, prima y comisión por afianzadora.',
      'Clic en una fila para ver el desglose de fianzas de esa afianzadora en un diálogo detallado.',
      'Indicador de "Prima mínima" visible en tarifa cuando aplica.',
    ],
  },
  {
    title: 'Mejoras en reportes y cálculos',
    date: '3 Feb 2026',
    tags: ['mejora', 'comisiones'],
    summary:
      'Anualización de tarifas en todos los reportes, detección de prima mínima y mejora en la extracción de prima.',
    bullets: [
      'Tarifas ahora se anualizan automáticamente en fianzas con vigencia mayor a un año en todos los reportes.',
      'Detección de "Prima mínima": las fianzas cuya tarifa excede el tramo más alto se identifican y etiquetan.',
      'El reporte de Tarifas por Cliente ahora excluye fianzas con prima mínima del cálculo de tarifa promedio.',
      'La extracción de prima ahora ignora valores en cero y continúa buscando en fuentes alternativas.',
      'Se muestra la prima extraída de la factura en el detalle de fianza cuando está disponible.',
    ],
  },
  {
    title: 'Tooltips en burbujas de responsables',
    date: '3 Feb 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Las burbujas de usuarios asignados ahora muestran el nombre completo al pasar el cursor.',
    bullets: [
      'Tooltip con nombre completo al hacer hover sobre cada burbuja de responsable.',
      'El indicador de overflow (+N) muestra los nombres de todos los usuarios restantes.',
      'Visible en tablas de Clientes, Contratos, Fianzas y tarjetas del Pipeline.',
    ],
  },
  {
    title: 'Responsables en tarjetas de Pulso',
    date: '3 Feb 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Las tarjetas del Pipeline Kanban en Pulso ahora muestran los responsables asignados.',
    bullets: [
      'Burbujas de responsables visibles en cada tarjeta del pipeline.',
      'Se combinan responsables del cliente y del contrato sin duplicados.',
    ],
  },
  {
    title: 'Filtro de fianzas requeridas pendientes en Contratos',
    date: '2 Feb 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nuevo filtro en la página de Contratos para mostrar solo contratos con fianzas requeridas que aún no han sido emitidas.',
    bullets: [
      'Nuevo dropdown "Fianzas" con opción "Con requeridas pendientes" para filtrar contratos.',
      'Filtra contratos donde al menos una fianza requerida no está vinculada a una fianza emitida.',
      'Mejora en el dropdown de selección de cliente: ahora se despliega correctamente debajo del botón con scroll funcional.',
    ],
  },
  {
    title: 'Nueva pestaña "Después del Término" en Pulso',
    date: '28 Ene 2026',
    tags: ['mejora'],
    summary:
      'Nueva pestaña en el dashboard de Pulso que muestra las fianzas requeridas con momento "después del término", permitiendo ver el estado de las fianzas hermanas del mismo contrato.',
    bullets: [
      'Tabla con cliente, contrato, tipo de fianza, estado y última vigencia de fianzas hermanas.',
      'Filas expandibles que muestran las fianzas hermanas del contrato con su tipo, momento, estado y vencimiento.',
      'Indicador "Lista para tramitar" cuando todas las fianzas hermanas ya fueron emitidas o pagadas.',
      'Tarjetas resumen con total, listas para tramitar y diferidas.',
      'Acciones de Diferir y Reactivar directamente desde la tabla.',
    ],
  },
  {
    title: 'Nueva pestaña de Uso en Configuración',
    date: '26 Ene 2026',
    tags: ['mejora', 'configuración', 'ux'],
    summary:
      'Nueva pestaña "Uso" en Configuración para visualizar el consumo de recursos del plan: páginas procesadas y almacenamiento.',
    bullets: [
      'Barras de progreso para páginas y almacenamiento con colores según nivel de uso (verde, amarillo, rojo).',
      'Detalles adicionales: páginas disponibles, almacenamiento disponible, archivos almacenados y costo por página extra.',
      'Modal "Ver detalle" con lista de todos los archivos almacenados, incluyendo nombre, cliente, tipo, tamaño y fecha.',
      'Tooltip informativo cuando el plan permite exceder límites con cargo adicional.',
    ],
  },
  {
    title: 'Integración de pagos con Stripe',
    date: '26 Ene 2026',
    tags: ['mejora', 'configuración'],
    summary:
      'Nueva integración con Stripe para gestionar suscripciones de forma segura y fluida.',
    bullets: [
      'Checkout integrado: proceso de pago directo con Stripe Checkout para suscripciones.',
      'Portal de cliente: acceso al portal de Stripe para administrar método de pago, recibos y cancelación.',
      'Estado de suscripción: visualización del estado actual (Activo, Prueba, Cancelado, etc.) en Configuración.',
      'Barra de uso: indicador visual de páginas procesadas vs. límite mensual con colores según el nivel.',
    ],
  },
  {
    title: 'Mejoras en recepción de documentos',
    date: '25 Ene 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Protección contra solicitudes excesivas y mejoras visuales en la página de carga de expedientes.',
    bullets: [
      'Límite de solicitudes: protección contra ataques de fuerza bruta con mensaje amigable al usuario.',
      'Pantalla de error rediseñada con logo de la marca y mensaje de seguridad.',
      'Pantalla de carga mejorada con logo y texto de estado.',
      'Optimización de rendimiento: se eliminaron llamadas duplicadas al servidor.',
    ],
  },
  {
    title: 'Exportar Reportes a PDF',
    date: '23 Ene 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nueva funcionalidad para exportar reportes a PDF con diseño profesional y soporte para múltiples reportes en un solo documento.',
    bullets: [
      'Botón "Exportar PDF" en la página de Reportes que abre un diálogo de selección.',
      'Selección múltiple: elige uno o varios reportes para exportar en un solo documento.',
      'Encabezado con logo de Surelia, título del reporte y período seleccionado.',
      'Pie de página con fecha de generación y numeración de páginas.',
      'Soporte para reportes largos con paginación automática sin cortar contenido.',
      'Los gráficos y tablas se capturan exactamente como se ven en pantalla.',
      'Filtros y controles interactivos se ocultan automáticamente en el PDF.',
    ],
  },
  {
    title: 'Reporte: Producción por Cliente',
    date: '23 Ene 2026',
    tags: ['mejora', 'comisiones'],
    summary:
      'Nuevo reporte que muestra la producción y comisiones por cliente, permitiendo identificar qué clientes generan más valor.',
    bullets: [
      'Treemap interactivo que visualiza la distribución de producción por cliente.',
      'Los clientes más grandes aparecen con mayor tamaño; nombres solo visibles en celdas grandes para mejor legibilidad.',
      'Tabla con contratos, fianzas, prima y comisión por cliente, ordenada por mayor producción.',
      'Filas expandibles para ver desglose mensual de cada cliente.',
      'Panel lateral con lista de fianzas al hacer clic en un mes específico.',
      'Filtro por mes o año completo.',
    ],
  },
  {
    title: 'Reporte: Tarifas por Cliente',
    date: '23 Ene 2026',
    tags: ['mejora', 'comisiones'],
    summary:
      'Nuevo reporte de análisis de tarifas promedio por cliente para identificar oportunidades de mejora en negociación.',
    bullets: [
      'Métricas globales: tarifa promedio general, total de clientes y fianzas.',
      'Gráfica de barras horizontales mostrando top 15 clientes por tarifa promedio.',
      'Código de colores: verde (arriba del promedio), azul (cerca), ámbar (bajo), rojo (muy bajo).',
      'Línea de referencia indicando el promedio general para fácil comparación.',
      'Tabla con prima total, monto afianzado, tarifa promedio y rango (mín-máx) por cliente.',
      'Filas expandibles mostrando cada fianza individual con su tarifa y comisión.',
      'Filtro por mes o año completo.',
    ],
  },
  {
    title: 'Permisos de edición de clientes',
    date: '21 Ene 2026',
    tags: ['bug'],
    summary:
      'Corrección de permisos que impedía a operadores generar links de expediente para clientes creados por otros usuarios.',
    bullets: [
      'Los operadores ahora pueden editar y eliminar cualquier cliente de su organización.',
      'Anteriormente solo podían modificar clientes que ellos mismos habían creado.',
      'Esto soluciona el problema al generar links de expediente en clientes compartidos.',
    ],
  },
  {
    title: 'Bitácora: registro de acciones en Pulso',
    date: '15 Ene 2026',
    tags: ['mejora'],
    summary:
      'Todas las acciones del Pipeline Kanban en Pulso ahora quedan registradas en la bitácora para auditoría completa.',
    bullets: [
      'Registro al iniciar proceso: incluye las afianzadoras y sus comentarios en la descripción.',
      'Registro al regresar fianza a pendientes desde En Proceso.',
      'Registro al diferir y reactivar fianzas requeridas.',
      'Registro al editar afianzadoras: muestra nombres y comentarios actualizados.',
      'Registro al marcar fianza como renovable o no renovable.',
      'Las fianzas pagadas ya no pueden moverse a "emitidas sin pagar" por seguridad.',
    ],
  },
  {
    title: 'Pulso: Pipeline Kanban y Fianzas Renovables',
    date: '15 Ene 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nueva funcionalidad de Pipeline Kanban para seguimiento de fianzas, gestión de afianzadoras en proceso, y sistema de fianzas renovables.',
    bullets: [
      'Pipeline Kanban: visualización de fianzas en columnas Pendientes, En Proceso, Emitidas sin Pagar y Pagadas.',
      'Diálogo "Iniciar Proceso": al mover una fianza a En Proceso, se capturan las afianzadoras con sus comentarios individuales.',
      'Edición de afianzadoras: las tarjetas En Proceso son expandibles y editables con botón de lápiz.',
      'Fianzas renovables: nuevo campo al crear fianzas, con indicador en el detalle de fianza, badge en fianzas emitidas del contrato, y filtro en Vencimientos.',
      'Conteo de clientes mejorado: clientes que son "incompletos" Y "vencidos" ahora se cuentan en ambas categorías.',
      'Interfaz simplificada: eliminado el selector de proceso del contrato que ya no se utilizaba.',
    ],
  },
  {
    title: 'Mejoras en diálogo de generación de links',
    date: '14 Ene 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Rediseño del diálogo para generar links de documentos con presets y mejor usabilidad.',
    bullets: [
      'Nuevo diseño con estructura DialogTitle, DialogBody y DialogActions.',
      'Presets rápidos: Contrato, Actualización, Todos y Ninguno para selección de documentos.',
      'Layout de 2 columnas para mejor visualización de los documentos requeridos.',
      'Filas clickeables: ahora se puede hacer clic en toda la fila para seleccionar.',
    ],
  },
  {
    title: 'Mejoras en recepción de documentos',
    date: '14 Ene 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nuevas validaciones y ayuda contextual en la página de carga de expedientes para mejorar la experiencia del usuario.',
    bullets: [
      'Validación de archivos vacíos: se rechazan archivos menores a 1KB para evitar documentos corruptos o vacíos.',
      'Nuevo ícono de ayuda (?) junto a cada tipo de documento con descripción al pasar el cursor.',
      'Tooltips explicativos para los 13 tipos de documentos requeridos (Acta Constitutiva, Poderes, etc.).',
    ],
  },
  {
    title: 'Bitácora: registro de asignación de responsables',
    date: '9 Ene 2026',
    tags: ['mejora'],
    summary:
      'La bitácora ahora registra automáticamente cuando se asignan o desasignan responsables a clientes y contratos.',
    bullets: [
      'Registro automático al asignar o desasignar usuarios de un cliente.',
      'Registro automático al asignar o desasignar usuarios de un contrato, indicando el nombre del contrato.',
      'Descripciones claras: "Asignó a Juan Pérez" o "Desasignó a María García en contrato X".',
      'Palabras clave con estilo visual: "Asignó" en verde y "Desasignó" en rojo para fácil identificación.',
    ],
  },
  {
    title: 'Bitácora: seguimiento de notas y revisión de documentos',
    date: '6 Ene 2026',
    tags: ['mejora'],
    summary:
      'La bitácora ahora registra cambios en las notas de clientes y la aceptación de documentos después de su revisión.',
    bullets: [
      'Registro automático en bitácora al modificar notas de un cliente, mostrando el valor anterior y el nuevo.',
      'Seguimiento de aceptación de documentos después de revisión en el expediente del cliente.',
    ],
  },
  {
    title: 'Mejoras en tablas y gestión de notas',
    date: '6 Ene 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Rediseño de las tablas de Clientes, Contratos y Fianzas con mejor organización de columnas, nuevos filtros de ordenamiento y gestión de notas integrada.',
    bullets: [
      'Columnas reorganizadas: Cliente/Empresa como primera columna, seguido de Responsables.',
      'Eliminadas columnas de "Estado" para una vista más limpia y enfocada.',
      'Nuevos filtros de ordenamiento en Contratos y Fianzas: fecha de creación, monto, cliente y más.',
      'Edición de responsables ahora requiere clic en el ícono de lápiz (no en toda la celda).',
      'Nueva columna de Notas en Clientes con edición directa desde la tabla.',
      'Búsqueda en Clientes ahora incluye el contenido de las notas.',
      'Registro en bitácora al modificar notas, incluyendo valor anterior y nuevo.',
    ],
  },
  {
    title: 'Bitácora de Actividad',
    date: '6 Ene 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Nueva página de Bitácora de Actividad con registro histórico de todas las acciones en el sistema en tiempo real.',
    bullets: [
      'Visualiza todas las creaciones, actualizaciones y eliminaciones en tiempo real.',
      'Filtros por colección (Clientes, Contratos, Enlaces), tipo de acción, usuario y cantidad de registros.',
      'Cada entrada muestra quién hizo la acción, qué hizo, el cliente involucrado y la fecha exacta.',
      'Acceso rápido desde el sidebar con ícono de reloj.',
    ],
  },
  {
    title: 'Fianzas Fiscales',
    date: '5 Ene 2026',
    tags: ['mejora'],
    summary:
      'Se deja de requerir fianza para crear fianzas por caso de Fianzas Fiscales.',
    bullets: [
      'Las fianzas ahora pueden crearse sin necesidad de tener una fianza previa.',
    ],
  },
  {
    title: 'Nuevo diseño de vistas de Contrato y Fianza',
    date: '3 Ene 2026',
    tags: ['mejora', 'ux'],
    summary:
      'Rediseño de las vistas de detalle de Contrato y Fianza con un layout de dos columnas más compacto y mejor organizado.',
    bullets: [
      'Layout de dos columnas: información del cliente y contrato a la izquierda, fianzas a la derecha.',
      'Tarjetas más compactas con mejor uso del espacio.',
      'Indicadores de vigencia y pago claramente etiquetados en las fianzas emitidas.',
      'Vista responsiva que se adapta correctamente en móvil.',
    ],
  },
  {
    title: 'Mejoras en Reportes',
    date: '1 Ene 2026',
    tags: ['mejora', 'bug', 'comisiones', 'ux'],
    summary:
      'Mejoras importantes en la experiencia de reportes con nuevos gráficos, mejor navegación y corrección de errores.',
    bullets: [
      'Corrección de bug: los reportes mostraban "0 resultados" cuando el año actual no tenía datos.',
      'Selector de año ahora se comparte entre todos los reportes para navegación más fluida.',
      'Producción Mensual: nuevo gráfico de barras de comisión mensual, vista "Resumen anual" por defecto.',
      'Comisión por Afianzadora: nuevo gráfico de líneas y fila de totales en la tabla.',
      'Nombres de afianzadoras simplificados: Aserta, Berkley, Tokio Marine.',
      'Filtros reposicionados junto al título para mejor accesibilidad.',
      '¡Y feliz año nuevo!',
    ],
  },
  {
    title: 'Reporte: Producción por Responsable',
    date: '31 Dic 2025',
    tags: ['mejora', 'comisiones'],
    summary:
      'Nuevo reporte que muestra la producción y comisiones estimadas por cada miembro del equipo, con desglose mensual interactivo.',
    bullets: [
      'Tabla con clientes, contratos, fianzas, prima y comisión por responsable.',
      'Gráfica de líneas mostrando la producción mensual de cada responsable.',
      'Filas expandibles para ver el desglose por mes de cada responsable.',
      'Panel lateral con lista de fianzas al hacer clic en un mes específico.',
      'Navegación directa a la fianza desde el panel lateral.',
      'Modo de atribución: Individual (100% a cada responsable) o Compartido (dividido equitativamente).',
      'Clientes y contratos sin responsable asignado se muestran como "Sin asignar".',
    ],
  },
  {
    title: 'Asignación de Responsables',
    date: '30 Dic 2025',
    tags: ['mejora', 'ux'],
    summary:
      'Nuevo sistema para asignar miembros de la organización como responsables de clientes y contratos. Los responsables asignados a un cliente heredan responsabilidad de todos los contratos y fianzas.',
    bullets: [
      'Burbujas con iniciales de usuarios en las tablas de Clientes, Contratos y Fianzas.',
      'Asignación directa desde las tablas haciendo clic en las burbujas.',
      'Los usuarios asignados al cliente aparecen bloqueados (no editables) al asignar responsables a un contrato.',
      'Colores consistentes por usuario basados en su identificador.',
      'Contador de usuarios mostrando desglose: "3 usuarios (1 del cliente, 2 del contrato)".',
      'Gestión de responsables también disponible desde la vista detallada de cliente y contrato.',
      'Notificaciones por correo electrónico cuando un usuario es asignado como responsable.',
    ],
  },
  {
    title: 'Facturación y Pagos',
    date: '29 Dic 2025',
    tags: ['mejora', 'configuración'],
    summary:
      'Nueva pestaña de Facturación en Configuración para gestionar datos fiscales y consultar el historial de pagos de la organización.',
    bullets: [
      'Datos de facturación editables: Razón Social, RFC, Código Postal, Régimen Fiscal y Uso CFDI.',
      'Catálogos SAT integrados con todos los regímenes fiscales y usos de CFDI vigentes.',
      'Historial de pagos con desglose automático de Subtotal, IVA (16%) y Total.',
      'Indicador de vigencia con fecha de expiración calculada por cada pago.',
      'Configuración reorganizada en 3 pestañas: Organización, Comisiones y Facturación.',
    ],
  },
  {
    title: 'Recuperación de contraseña personalizada',
    date: '26 Dic 2025',
    tags: ['mejora', 'ux'],
    summary:
      'Nueva experiencia de recuperación de contraseña con páginas personalizadas en el dominio de Surelia, reemplazando las páginas genéricas de Firebase.',
    bullets: [
      'Página de "Olvidé mi contraseña" rediseñada con estilo consistente al login.',
      'Nueva página /auth/action que maneja reset de contraseña, verificación de email y recuperación de email.',
      'Template de email HTML personalizado con branding de Surelia, compatible con modo oscuro.',
      'Mensajes de error y éxito en español con mejor feedback al usuario.',
    ],
  },
  {
    title: 'Organizaciones y Roles',
    date: '24 Dic 2025',
    tags: ['mejora', 'configuración'],
    summary:
      'Nueva estructura organizacional que permite gestionar equipos de trabajo con roles diferenciados. Los usuarios ahora pertenecen a una organización con permisos específicos según su rol.',
    bullets: [
      'Propietarios: Acceso completo a clientes, reportes, tarifas de comisiones y gestión de usuarios.',
      'Operadores: Acceso a clientes y operación diaria, sin acceso a reportes ni configuración de tarifas.',
      'Gestión de equipo centralizada en Configuración con creación de usuarios simplificada.',
      'Tarifas de comisiones ahora compartidas a nivel organización para todos los propietarios.',
    ],
  },
  {
    title: 'Matching estricto de comisiones',
    date: '23 Dic 2025',
    tags: ['mejora', 'comisiones'],
    summary:
      'Ahora los reportes usan una lógica de matching estricta para tablas de comisiones: primero busca coincidencia exacta Emisor + SubRamo + Tipo y, si no existe, cae a la tabla genérica del emisor. No se usan coincidencias parciales.',
    bullets: [
      'Emisor debe coincidir exactamente; SubRamo y Tipo deben coincidir ambos para aplicar una tabla específica.',
      'Sin match exacto, se usa la tabla genérica (sin SubRamo ni Tipo) de ese emisor como respaldo.',
      'El tramo de comisión se elige solo si la tarifa cae dentro de sus límites; si no, no se asigna comisión.',
    ],
  },
  {
    title: 'Gestión de SubRamo y Tipo en Configuración',
    date: '23 Dic 2025',
    tags: ['configuración'],
    summary:
      'Se alineó el manejo de SubRamo/Tipo: las tablas con solo uno de los campos ya no participan en el cálculo, y se recomienda siempre crear una tabla genérica por afianzadora como fallback.',
    bullets: [
      'Tablas específicas requieren SubRamo y Tipo; tablas con solo uno de los dos se ignoran con matching estricto.',
      'Se mantiene la recomendación de crear una tabla genérica (sin SubRamo/Tipo) por afianzadora para asegurar cálculo.',
      'Selector de SubRamo se alimenta dinámicamente por afianzadora para evitar errores de captura.',
    ],
  },
  {
    title: 'UX de tablas de comisiones',
    date: '23 Dic 2025',
    tags: ['ux', 'mejora'],
    summary:
      'Mejoras en Configuración para agilizar la creación y visualización de tablas de comisiones.',
    bullets: [
      'El formulario de nueva tabla ahora se despliega bajo demanda y se cierra al guardar/cancelar.',
      'La tabla recién creada se muestra expandida automáticamente para revisar rangos de inmediato.',
      'Las nuevas tablas aparecen en segundos sin acciones extra; eliminamos recargas manuales que generaban demoras.',
    ],
  },
];

const Changelog = () => {
  const [activeTab, setActiveTab] = useState('actualizaciones');

  const tabs = [
    { id: 'actualizaciones', label: 'Actualizaciones', count: actualizaciones.length },
    { id: 'desarrollo', label: 'En desarrollo', count: enDesarrollo.length },
    { id: 'problemas', label: 'Problemas conocidos', count: problemasConocidos.length },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-azul-surelia/15">
        <Heading level={1} className="text-azul-surelia mb-2">Avisos de la plataforma</Heading>
        <Text className="text-azul-surelia/70">
          Novedades sobre cambios, mejoras, problemas conocidos y futuras actualizaciones. Última actualización: 13 Mar 2026.
        </Text>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-azul-surelia/15 overflow-hidden">
        <div className="border-b border-azul-surelia/15">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-azul-surelia text-white border-b-2 border-azul-surelia'
                    : 'text-azul-surelia/70 hover:text-azul-surelia hover:bg-azul-surelia/5'
                }`}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {activeTab === 'desarrollo' && (
            <div className="space-y-4">
              {[...enDesarrollo]
                .sort((a, b) => {
                  const order = { alta: 0, media: 1, baja: 2 };
                  return (order[a.priority] ?? 3) - (order[b.priority] ?? 3);
                })
                .map((item) => (
                <div key={item.title} className="border border-azul-surelia/10 rounded-lg p-4 bg-amber-50/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Heading level={3} className="text-azul-surelia text-lg">{item.title}</Heading>
                    {item.priority && (
                      <Badge color={tagColors[item.priority] || 'zinc'}>
                        Prioridad: {item.priority}
                      </Badge>
                    )}
                  </div>
                  <Text className="text-azul-surelia/70">{item.description}</Text>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'actualizaciones' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {actualizaciones.map((item) => (
                <div
                  key={`${item.title}-${item.date}`}
                  className="border border-azul-surelia/10 rounded-lg p-5 space-y-3 bg-green-50/30"
                >
                  <div className="flex items-start justify-between gap-3">
                    <Heading level={3} className="text-azul-surelia text-lg">
                      {item.title}
                    </Heading>
                    <span className="text-sm font-semibold text-azul-surelia/60 whitespace-nowrap">
                      {item.date}
                    </span>
                  </div>
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {item.tags.map((tag) => (
                        <Badge key={tag} color={tagColors[tag] || 'zinc'}>
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Text className="text-azul-surelia/80">{item.summary}</Text>
                  <ul className="list-disc list-inside space-y-2 text-sm text-azul-surelia/90">
                    {item.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'problemas' && (
            <div className="space-y-4">
              {problemasConocidos.map((item) => (
                <div
                  key={item.title}
                  className="border border-red-200 rounded-lg p-5 space-y-3 bg-red-50/50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <Heading level={3} className="text-azul-surelia text-lg">
                      {item.title}
                    </Heading>
                    <span className="text-sm font-semibold text-azul-surelia/60 whitespace-nowrap">
                      {item.date}
                    </span>
                  </div>
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {item.tags.map((tag) => (
                        <Badge key={tag} color={tagColors[tag] || 'zinc'}>
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <Text className="text-azul-surelia/80 font-medium">{item.summary}</Text>
                  <ul className="list-disc list-inside space-y-2 text-sm text-azul-surelia/90">
                    {item.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


export default Changelog; 
